import { useState, useEffect, useCallback } from "react";
import { FileService } from "../../../services/SubmissionService";
import * as APIModels from "../../../models";
import pdfIcon from "../../../assets/images/file-icons/pdf.svg";
import zipIcon from "../../../assets/images/file-icons/zip.svg";
import Select, { ActionMeta } from "react-select";
import { Globals } from "../../../helpers/globals";
import { useDropzone } from "react-dropzone";
import { formatBytes } from "../../../helpers/utility";
import { Endpoints } from "../../../helpers/Endpoints";
import React from "react";
import {
    ErrorModal,
    ErrorModalProps,
} from "../../../components/shared/modals/ErrorModal";

type NameValueOptionType = { label: string; value: number };

export default function NewCaseDocuments({
    callback,
}: {
    callback: (documents: APIModels.Document[]) => void;
}) {
    const [completedDocuments, setCompletedDocuments] = useState(
        [] as APIModels.Document[]
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [show, setShow] = useState(false);
    const filenameRegex = /[^a-zA-Z0-9_-]+/g;
    const onCloseErrorModal = () => {
        setShow(false);
        setErrorMessage("");
        setErrorModalProps({
            onClose: onCloseErrorModal,
        } as ErrorModalProps);

        // refresh the page
        // setRefresh(true);
    };

    const [errorModalProps, setErrorModalProps] = useState({
        errorMessage: "",
        onClose: onCloseErrorModal,
    } as ErrorModalProps);

    const currentUser = Globals.currentUserValue;

    useEffect(() => {
        if (errorMessage !== "") {
            setErrorModalProps({
                ...errorModalProps,
                errorMessage: errorMessage,
            });
            setShow(true);
        } else {
            setShow(false);
        }
    }, [errorMessage]);

    const onDrop = useCallback((acceptedFiles) => {
        acceptedFiles.forEach((file: any) => {
            FileService.upload({
                fileServiceUrl: Endpoints.FileManager ?? "",
                file: file,
                filename: "uploads/" + file.name,
            }).then(
                (resp) => {
                    const temp = [
                        ...completedDocuments,
                        { filename: file.name },
                    ];

                    setCompletedDocuments([
                        ...completedDocuments,
                        { filename: file.name },
                    ]);
                },
                (error) => {
                    console.log(error);
                }
            );
        });
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        maxSize: 35000000,
        accept: "application/pdf",
        multiple: true,
    });

    const onDocumentsCompete = () => {
        <></>;
    };
    /*
    const onFileUpload = (event: React.FormEvent) => {
        event.preventDefault();
        const input = event.target as HTMLInputElement;

        if (!input.files?.length) {
            return;
        }

        const temp = [...completedDocuments];
        for (let i = 0; i < input.files.length; i++) {
            const f = input.files[i];
            if (!f.name.endsWith(".pdf")) {
                setErrorMessage("Invalid File Type. Please Upload a PDF");
                return;
            }

            FileService.upload({ fileServiceUrl: Endpoints.FileManager, file: f, filename: "uploads/" + f.name }).then(
                (resp) => {
                    temp.push({ filename: f.name, size: f.size });
                    setCompletedDocuments(temp);

                    input.value = "";
                },
                (error) => {
                    console.log(error);
                    input.value = "";
                }
            );
        }
    };
*/

    const onFileUpload = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            const input = event.target as HTMLInputElement;

            if (!input.files?.length) {
                return;
            }

            const temp = [] as APIModels.FileInfo[];

            for (let i = 0; i < input.files.length; i++) {
                const f = input.files[i];
                if (!f.name.endsWith(".pdf")) {
                    setErrorMessage("Invalid File Type. Please Upload a PDF");
                    return;
                }
                const sanatizedName =
                    f.name
                        .toLowerCase()
                        .split(".pdf")[0]
                        .replace(filenameRegex, "") + ".pdf";

                FileService.upload({
                    fileServiceUrl: Endpoints.FileManager ?? "",
                    file: f,
                    filename: "uploads/" + sanatizedName,
                }).then(
                    (resp) => {
                        temp.push({
                            filename: sanatizedName,
                            size: f.size,
                            lastUpdatedDateTime: new Date(Date.now()),
                        });
                        setCompletedDocuments([...completedDocuments, ...temp]);

                        input.value = "";
                    },
                    (error) => {
                        console.log(error);
                        input.value = "";
                    }
                );
            }
        },
        [completedDocuments]
    );

    const onValidate = () => {
        // are there docs to check?
        // if so, do they have doctypes?
        if (completedDocuments.length === 0) {
            setErrorMessage("Please upload a document");
            return false;
        } else {
            for (const doc of completedDocuments) {
                if (doc.documentType == "" || doc.documentType == undefined) {
                    setErrorMessage(
                        "Please label documents with a document type."
                    );
                    return false;
                }
            }

            // check if Complaint is attached
            if (
                !completedDocuments?.find((element) => {
                    return element.documentType === "Complaint";
                })
            ) {
                setErrorMessage(
                    "A Complaint is required for an initial case filing."
                );
                return false;
            }

            // check if Summons is attached
            if (
                !completedDocuments?.find((element) => {
                    return element.documentType === "Summons";
                })
            ) {
                if (confirm("You have not uploaded a Summons. Is that OK?")) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        }
    };

    const onNext = () => {
        console.log("OnNext");
        if (onValidate() === true) {
            callback(completedDocuments);
        }
    };

    const onDelete = (file: APIModels.FileInfo) => {
        if (file.filename) {
            FileService.delete({ filename: file.filename }).then(
                (resp) => {
                    const temp = [...completedDocuments];
                    temp.splice(temp.indexOf(file), 1);
                    setCompletedDocuments(temp);
                    console.log(resp);
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    };

    const onDocumentTypeChange = (index: number, value: string) => {
        const temp = [...completedDocuments];
        temp[index].documentType = value;

        setCompletedDocuments(temp);
        return false;
    };

    return (
        <>
            {show && <ErrorModal {...errorModalProps} />}
            <div className="card-body">
                <div className="row">
                    <div className="col-sm-6">
                        <p className="sub-header">
                            Please drop and label any documents for your initial
                            case filing. This includes the required
                            complaint/petition or any summons.
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        <div
                            {...getRootProps({
                                className: "dropzone card-body",
                            })}
                        >
                            <input {...getInputProps()} />

                            <div className="fileupload btn btn-success waves-effect waves-light mb-3">
                                <span>
                                    <i className="mdi mdi-cloud-upload me-1"></i>{" "}
                                    Upload Files
                                </span>
                                <input
                                    type="file"
                                    className="upload"
                                    onChange={(event) => {
                                        onFileUpload(event);
                                        event.target.value = "";
                                    }}
                                    multiple
                                />
                            </div>
                            <div>* 35 MB Maximum Upload Size</div>
                            <div
                                className="table-responsive"
                                style={{ overflow: "visible" }}
                            >
                                <table
                                    className="table table-centered  table-nowrap mb-0"
                                    style={{ zIndex: 0 }}
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th scope="col">Type</th>
                                            <th scope="col">File Name</th>
                                            <th scope="col">Size</th>
                                            <th
                                                scope="col"
                                                className="text-center"
                                                style={{ width: 125 }}
                                            >
                                                Action
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completedDocuments.map((file, i) => {
                                            return (
                                                <tr>
                                                    <td>
                                                        <select
                                                            onChange={(e) =>
                                                                onDocumentTypeChange(
                                                                    i,
                                                                    e
                                                                        .currentTarget
                                                                        .value
                                                                )
                                                            }
                                                        >
                                                            <option
                                                                selected={
                                                                    !file.documentType
                                                                }
                                                                value=""
                                                            ></option>
                                                            <option
                                                                selected={
                                                                    file.documentType ===
                                                                    "Complaint"
                                                                }
                                                                value="Complaint"
                                                            >
                                                                Complaint
                                                            </option>
                                                            <option
                                                                selected={
                                                                    file.documentType ===
                                                                    "Summons"
                                                                }
                                                                value="Summons"
                                                            >
                                                                Summons
                                                            </option>
                                                            <option
                                                                selected={
                                                                    file.documentType ===
                                                                    "Other"
                                                                }
                                                                value="Other"
                                                            >
                                                                Other
                                                            </option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <img
                                                            src={
                                                                file.filename &&
                                                                file.filename
                                                                    .toLowerCase()
                                                                    .endsWith(
                                                                        ".pdf"
                                                                    )
                                                                    ? pdfIcon
                                                                    : zipIcon
                                                            }
                                                            height="30"
                                                            alt="icon"
                                                            className="me-2"
                                                        />
                                                        <a
                                                            href="javascript:void(0);"
                                                            className="text-dark"
                                                        >
                                                            {file.filename}
                                                        </a>
                                                    </td>
                                                    <td>
                                                        {formatBytes(
                                                            file.size ?? 0
                                                        )}
                                                    </td>
                                                    <td>
                                                        <ul
                                                            className="list-inline table-action m-0"
                                                            style={{
                                                                display:
                                                                    "inline-block",
                                                            }}
                                                        >
                                                            <li className="list-inline-item">
                                                                <a
                                                                    href="javascript:void(0);"
                                                                    className="action-icon px-1"
                                                                    onClick={() =>
                                                                        onDelete(
                                                                            file
                                                                        )
                                                                    }
                                                                >
                                                                    {" "}
                                                                    <i className="mdi mdi-delete"></i>
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className="row mt-3">
                                    <div
                                        className="col-12"
                                        style={{ textAlign: "center" }}
                                    >
                                        <button
                                            className="btn btn-success"
                                            type="submit"
                                            style={{
                                                right: 24,
                                                bottom: 40,
                                                position: "absolute",
                                            }}
                                            onClick={onNext}
                                        >
                                            Next
                                            <i className="mdi mdi-arrow-right"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
